import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import Filters from './Filters';
import ReportTable from './Table';
import { UsageReportRow } from './Table';
import { getUsageReportData } from '../../api/UsageReport';
const NOVO_NORDISK = 'novo nordisk';

function isNovoNordisk(organization: string): boolean {
  const normalized = organization.trim().toLowerCase().replace(/\s+/g, '');
  return normalized === NOVO_NORDISK.replace(/\s+/g, '');
}

/** Parse company list from REACT_APP_DOMAIN_ORGANIZATION_MAP (JSON array). */
function getCompaniesFromEnv(): string[] {
  try {
    const raw = process.env.REACT_APP_DOMAIN_ORGANIZATION_MAP;
    if (!raw || typeof raw !== 'string') return [];
    const parsed = JSON.parse(raw.trim());
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item: unknown) => String(item)).filter(Boolean).sort();
  } catch {
    return [];
  }
}



const companiesFromEnv = getCompaniesFromEnv();

const UsageReport: React.FC = () => {
  const [company, setCompany] = useState<string>('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [filteredData, setFilteredData] = useState<UsageReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Clear dashboard when company filter changes so results don't persist until Search is clicked
  useEffect(() => {
    setFilteredData([]);
    setError('');
  }, [company]);

  const handleSearch = () => {
    // Validate dates before searching
    if (!dateRange.from || !dateRange.to) {
      setError('Please select both From and To dates');
      return;
    }
    if (dateRange.from > dateRange.to) {
      setError('From date must be before or equal to To date');
      return;
    }
    // Manually trigger fetch with current filter values
    fetchReportData();
  };

  const handleResetFilters = () => {
    setCompany('');
    setDateRange({ from: '', to: '' });
    setFilteredData([]);
    setError('');
  };

  const convertDateToISO = (dateString: string, isEndDate: boolean = false): string => {
    if (!dateString) return '';
    
    // Parse the date string (YYYY-MM-DD) and create a UTC date
    // For start date: add T00:00:00Z to start at midnight UTC
    // For end date: add T23:59:59Z to end at end of day UTC
    const timeComponent = isEndDate ? 'T23:59:59Z' : 'T00:00:00Z';
    const utcDateString = `${dateString}${timeComponent}`;
    
    const date = new Date(utcDateString);
    return date.toISOString();
  };

  const fetchReportData = async () => {
    setLoading(true);
    setError('');
    try {
      // Convert dates with proper UTC handling
      // from: 2026-01-24T00:00:00Z (start of day UTC)
      // to: 2026-01-25T23:59:59Z (end of day UTC)
      const fromISO = convertDateToISO(dateRange.from, false);
      const toISO = convertDateToISO(dateRange.to, true);

      const response = await getUsageReportData({
        organization: company || '',
        from: fromISO,
        to: toISO
      });

      // Transform DataDog RUM data to UsageReportRow format
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      setFilteredData(rows as UsageReportRow[]);
    } catch (err: any) {
      console.error('Failed to fetch usage report data:', err);
      setError(err.message || 'Failed to fetch data from DataDog');
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };


  /** Escape a CSV cell per RFC 4180: double internal quotes, wrap in quotes. */
  const escapeCsvCell = (value: string | number | undefined | null): string => {
    const text = value === undefined || value === null ? '' : String(value);
    return '"' + text.replace(/"/g, '""') + '"';
  };

  const convertToCSV = (data: UsageReportRow[]): string => {
    const showUserColumns = isNovoNordisk(company);
    const headers = showUserColumns
      ? ['User ID', 'Username', 'Email', 'Organization', 'Last Used Date', 'Total Time Spent (hrs)']
      : ['User ID', 'Organization', 'Last Used Date', 'Total Time Spent (hrs)'];

    const rows = data.map(row => {
      const base = [
        row.user_id,
        ...(showUserColumns ? [row.username ?? '', row.email ?? ''] : []),
        row.organization,
        new Date(row.last_used_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }),
        row.total_time_spent_hours.toFixed(2)
      ];
      return base;
    });

    const csvContent = [
      headers.map(h => escapeCsvCell(h)).join(','),
      ...rows.map(row => row.map(cell => escapeCsvCell(cell)).join(','))
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = () => {
    const csvContent = convertToCSV(filteredData);
    const element = document.createElement('a');
    const file = new Blob([csvContent], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = `usage-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(element);
    element.click();
    URL.revokeObjectURL(element.href);
    document.body.removeChild(element);
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4, pt: 12 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mt: '20px' }}>
              Usage Report
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={downloadCSV}
              disabled={filteredData.length === 0 || loading}
              sx={{ mt: '20px' }}
            >
              Download as CSV
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'white' }}>
            <Filters
              company={company}
              setCompany={setCompany}
              dateRange={dateRange}
              setDateRange={setDateRange}
              companies={companiesFromEnv}
              onSearch={handleSearch}
              onReset={handleResetFilters}
              loading={loading}
            />
          </Paper>

          {!loading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Total users: {filteredData.length}
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper elevation={2} sx={{ bgcolor: 'white', overflow: 'auto' }}>
              <ReportTable
                data={filteredData}
                showUsernameEmail={isNovoNordisk(company)}
              />
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default UsageReport;